import { axiosApiInstance } from "../axios";

const DEFAULT_ERROR_MESSAGE = "Could not generate the product barcode PDF.";

export class ProductBarcodeApiError extends Error {
  constructor(
    message: string,
    public readonly status?: number
  ) {
    super(message);
    this.name = "ProductBarcodeApiError";
  }
}

function filenameFromDisposition(disposition: unknown, productId: string): string {
  if (typeof disposition !== "string") return `product-${productId}-barcode.pdf`;

  const encodedFilename = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encodedFilename) {
    try {
      return decodeURIComponent(encodedFilename.trim());
    } catch {
      // Fall through to the regular filename or the safe default.
    }
  }

  const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1]?.trim();
  return filename || `product-${productId}-barcode.pdf`;
}

async function errorMessageFromResponse(data: unknown): Promise<string> {
  let payload = data;

  if (payload instanceof Blob) {
    try {
      payload = JSON.parse(await payload.text());
    } catch {
      return DEFAULT_ERROR_MESSAGE;
    }
  } else if (typeof payload === "string") {
    const stringPayload = payload;
    try {
      payload = JSON.parse(stringPayload);
    } catch {
      return stringPayload || DEFAULT_ERROR_MESSAGE;
    }
  }

  if (payload && typeof payload === "object" && "message" in payload) {
    const message = (payload as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message;
  }

  return DEFAULT_ERROR_MESSAGE;
}

function isApiError(error: unknown): error is {
  isAxiosError: true;
  response?: { data?: unknown; status?: number };
} {
  return Boolean(
    error &&
    typeof error === "object" &&
    "isAxiosError" in error &&
    (error as { isAxiosError?: unknown }).isAxiosError === true
  );
}

export async function getProductBarcodePdf(productId: string): Promise<{
  blob: Blob;
  filename: string;
}> {
  try {
    const response = await axiosApiInstance.post<Blob>(
      `products/${productId}/barcode-pdf`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken") ?? ""}`,
        },
        responseType: "blob",
      }
    );

    return {
      blob: response.data,
      filename: filenameFromDisposition(response.headers["content-disposition"], productId),
    };
  } catch (error: unknown) {
    if (isApiError(error)) {
      throw new ProductBarcodeApiError(
        await errorMessageFromResponse(error.response?.data),
        error.response?.status
      );
    }

    throw new ProductBarcodeApiError(DEFAULT_ERROR_MESSAGE);
  }
}

export function downloadProductBarcodePdf(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const downloadLink = document.createElement("a");
  downloadLink.href = objectUrl;
  downloadLink.download = filename;
  downloadLink.style.display = "none";
  document.body.appendChild(downloadLink);
  downloadLink.click();
  downloadLink.remove();

  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}
